import { useEffect, useRef } from 'react'
import {
  Mail,
  Download,
  MapPin,
  Cake,
  Activity,
  Github,
  Linkedin,
} from 'lucide-react'
import { PROFILE, MARQUEE_TECH } from '../data/profile'
import { FEATURED_PROJECTS } from '../data/projects'
import { useCVModal } from '../contexts/CVModalContext'
import { initGSAP, prefersReducedMotion, scrollToSection } from '../lib/motion'

const CONTACT_LINKS: {
  label: string
  icon: typeof Mail
  href: string
  external: boolean
}[] = [
  {
    label: 'Email',
    icon: Mail,
    href: `mailto:${PROFILE.email}`,
    external: false,
  },
  {
    label: 'LinkedIn',
    icon: Linkedin,
    href: 'https://www.linkedin.com/in/ezzddine-mhamdi/',
    external: true,
  },
  {
    label: 'GitHub',
    icon: Github,
    href: PROFILE.github,
    external: true,
  },
]

const STATS: { value: string; label: string }[] = [
  { value: '2+', label: 'years in DevOps & cloud' },
  { value: String(FEATURED_PROJECTS.length), label: 'production case studies' },
  { value: `${MARQUEE_TECH.length}+`, label: 'tools & platforms' },
]

const HEADLINE = [
  { word: 'Junior', gradient: false },
  { word: 'Platform', gradient: true },
  { word: 'Engineer', gradient: true },
]

const GLOW_THEMES = [
  { x: 42, y: -26, scale: 1.18, opacity: 0.9, duration: 11 },
  { x: -34, y: 24, scale: 1.1, opacity: 0.8, duration: 9 },
  { x: 26, y: 30, scale: 1.14, opacity: 0.85, duration: 13 },
]

const About: React.FC = () => {
  const { openModal } = useCVModal()
  const ref = useRef<HTMLElement>(null)
  const ringRef = useRef<HTMLDivElement>(null)
  const avatarWrapRef = useRef<HTMLDivElement>(null)
  const portraitRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      const tl = gsapApi.timeline({ defaults: { ease: 'power3.out' } })

      tl.fromTo(
        '[data-about-mask] > span',
        { yPercent: 115 },
        { yPercent: 0, duration: 0.8, stagger: 0.06 },
        0.15
      )
        .fromTo(
          '[data-about-heading]',
          { opacity: 0 },
          { opacity: 1, duration: 0.3 },
          0.1
        )
        .fromTo(ringRef.current, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.8 }, 0.1)
        .fromTo(
          portraitRef.current,
          { clipPath: 'circle(0% at 50% 50%)' },
          { clipPath: 'circle(75% at 50% 50%)', duration: 1.1, ease: 'power2.inOut' },
          0.35
        )
        .fromTo(
          '[data-about-fade]',
          { opacity: 0, y: 16, filter: 'blur(6px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, stagger: 0.06 },
          0.3
        )

      const xTo = gsapApi.quickTo(portraitRef.current, 'x', {
        duration: 0.8,
        ease: 'power2.out',
      })
      const yTo = gsapApi.quickTo(portraitRef.current, 'y', {
        duration: 0.8,
        ease: 'power2.out',
      })

      const onMove = (e: MouseEvent) => {
        const dx = e.clientX / window.innerWidth - 0.5
        const dy = e.clientY / window.innerHeight - 0.5
        xTo(dx * 18)
        yTo(dy * 18)
      }

      ref.current?.addEventListener('mousemove', onMove)

      const scrollTween = gsapApi.fromTo(
        avatarWrapRef.current,
        { y: 0 },
        {
          y: -48,
          ease: 'none',
          scrollTrigger: {
            trigger: ref.current,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        }
      )

      gsapApi.utils
        .toArray<HTMLElement>('[data-glow-blob]')
        .forEach((el, i) => {
          const theme = GLOW_THEMES[i % GLOW_THEMES.length]
          gsapApi.to(el, {
            x: theme.x,
            y: theme.y,
            scale: theme.scale,
            opacity: theme.opacity,
            duration: theme.duration,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
            delay: i * 0.8,
          })
        })

      gsapApi.utils
        .toArray<HTMLElement>('[data-about-metric]')
        .forEach((el) => {
          const match = (el.dataset.metric || '').match(/^(\d+)(.+)?$/)
          if (!match) return
          const target = { value: 0 }
          gsapApi.fromTo(
            target,
            { value: 0 },
            {
              value: Number(match[1]),
              duration: 1.4,
              ease: 'power2.out',
              scrollTrigger: { trigger: el, start: 'top 88%', once: true },
              onUpdate: () => {
                el.textContent = `${Math.round(target.value)}${match[2] ?? ''}`
              },
            }
          )
        })

      return () => {
        ref.current?.removeEventListener('mousemove', onMove)
        scrollTween.scrollTrigger?.kill()
        scrollTween.kill()
      }
    }, ref)
    return () => ctx.revert()
  }, [])

  return (
    <section id="about" ref={ref} className="relative overflow-hidden pt-20 sm:pt-24">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          data-glow-blob
          className="absolute -top-40 right-[-12%] h-96 w-96 sm:h-[34rem] sm:w-[34rem] rounded-full bg-primary-400/15 dark:bg-primary-500/10 blur-3xl will-change-transform"
        />
        <div
          data-glow-blob
          className="absolute bottom-16 left-[-14%] h-72 w-72 rounded-full bg-primary-400/10 dark:bg-primary-500/[0.06] blur-3xl will-change-transform"
        />
        <div
          data-glow-blob
          className="absolute -bottom-32 right-[8%] h-80 w-80 rounded-full bg-primary-400/[0.07] blur-3xl will-change-transform"
        />
        <div className="noisy absolute inset-0" />
      </div>

      <div className="relative container-width grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-center pb-12 sm:pb-16">
        <div>
          <div data-about-fade className="mb-4">
            <p className="section-label">About</p>
          </div>

          <p data-about-fade className="mono-label !text-primary-500 mb-3 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-primary-500/60" />
            {PROFILE.tagline}
          </p>

          <h2
            data-about-heading
            className="font-display text-display-lg font-semibold tracking-tight"
          >
            {HEADLINE.map(({ word, gradient }) => (
              <span
                key={word}
                data-about-mask
                className="inline-flex overflow-hidden align-top mr-[0.22em] last:mr-0"
              >
                <span className={`inline-block will-change-transform ${gradient ? 'text-gradient' : ''}`}>
                  {word}
                </span>
              </span>
            ))}
          </h2>

          <div data-about-fade className="mt-5 space-y-4 text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-2xl">
            <p>
              I'm {PROFILE.name}, a junior platform engineer based in{' '}
              {PROFILE.location.replace(', North Africa', '')}. I build cloud
              infrastructure, Kubernetes platforms, GitOps workflows, and
              developer self-service systems.
            </p>
            <p>
              I care about platforms that make engineers faster without making
              the environment less safe — infrastructure reviewed like code,
              identity and secrets handled properly, and telemetry shipped from
              day one.
            </p>
            <p>
              Between 2024 and 2026 I worked as a Platform Engineer at
              Codincloud — two years of cloud infrastructure, Kubernetes
              platforms, GitOps workflows, and developer self-service systems.
              I've since completed my National Engineering Diploma in Cloud
              Engineering at EPI Digital School, and I'm now open to my next
              platform engineering role.
            </p>
          </div>

          <div data-about-fade className="mt-7 grid grid-cols-3 gap-4 sm:gap-6 border-t border-neutral-200 dark:border-white/8 pt-5 max-w-2xl">
            {STATS.map((stat) => (
              <div key={stat.label}>
                <p
                  data-about-metric
                  data-metric={stat.value}
                  className="font-display text-display-md font-semibold text-neutral-900 dark:text-white"
                >
                  {stat.value}
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400 leading-relaxed">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>

          <div data-about-fade className="mt-6 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollToSection('#contact')}
              className="btn-primary px-5 py-2.5 text-sm"
            >
              <Mail size={16} />
              Email me
            </button>
            <button
              onClick={openModal}
              className="btn-secondary px-5 py-2.5 text-sm"
            >
              <Download size={16} />
              Download CV
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 lg:pr-6">
          <div
            ref={avatarWrapRef}
            className="group relative mx-auto w-full max-w-[260px] will-change-transform"
          >
            <div
              ref={ringRef}
              aria-hidden="true"
              className="absolute -inset-2.5 rounded-full"
            >
              <div
                className="absolute inset-0 animate-spin-slow rounded-full"
                style={{
                  background:
                    'conic-gradient(from 0deg, transparent 0deg, rgba(34,211,238,0.5) 70deg, rgba(34,211,238,0.12) 120deg, transparent 190deg, transparent 360deg)',
                }}
              />
            </div>

            <div
              aria-hidden="true"
              className="absolute -inset-8 rounded-full bg-primary-500/20 blur-3xl"
            />

            <div
              ref={portraitRef}
              className="relative aspect-square overflow-hidden rounded-full ring-1 ring-white/25 dark:ring-white/15 shadow-2xl shadow-primary-500/25 will-change-transform transition-transform duration-500 group-hover:scale-[1.02]"
            >
              <img
                src={PROFILE.photo}
                alt={`Portrait of ${PROFILE.name}`}
                width={640}
                height={640}
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div
            data-about-fade
            className="relative overflow-hidden rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900 shadow-soft dark:shadow-none"
          >
            <div
              aria-hidden="true"
              className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent"
            />
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary-500/10 text-primary-500">
                    <Activity size={14} />
                  </span>
                  <span className="mono-label">Status</span>
                </div>
                <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.05] px-3.5 py-3">
                <span className="relative flex h-2 w-2 mt-1 flex-shrink-0" aria-hidden="true">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span>
                  <span className="block mono-label !text-[10px] !text-emerald-600 dark:!text-emerald-400">
                    Available for new roles
                  </span>
                  <span className="mt-1 block text-[13px] font-medium leading-snug text-neutral-800 dark:text-neutral-100">
                    {PROFILE.availability}
                  </span>
                </span>
              </div>

              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-3 py-2.5 border-b border-neutral-200 dark:border-white/6 last:border-0">
                  <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-neutral-50 border border-neutral-200 dark:border-white/8 dark:bg-white/[0.03] text-neutral-500 dark:text-neutral-400 flex-shrink-0">
                    <MapPin size={13} />
                  </span>
                  <span className="mono-label !text-[10px] !text-neutral-400 dark:!text-neutral-500 w-16 flex-shrink-0">
                    Location
                  </span>
                  <span className="text-[13px] font-medium text-neutral-800 dark:text-neutral-100">
                    Tunisia, Monastir
                  </span>
                </div>
                <div className="flex items-center gap-3 py-2.5">
                  <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-neutral-50 border border-neutral-200 dark:border-white/8 dark:bg-white/[0.03] text-neutral-500 dark:text-neutral-400 flex-shrink-0">
                    <Cake size={13} />
                  </span>
                  <span className="mono-label !text-[10px] !text-neutral-400 dark:!text-neutral-500 w-16 flex-shrink-0">
                    Age
                  </span>
                  <span className="text-[13px] font-medium text-neutral-800 dark:text-neutral-100">
                    25 years
                  </span>
                </div>
              </div>

              <div className="my-3 border-t border-neutral-200 dark:border-white/6" />

              <div className="flex items-center justify-between">
                <span className="mono-label !text-[10px] !text-neutral-400 dark:!text-neutral-500">
                  Get in touch
                </span>
                <div className="flex items-center gap-2">
                  {CONTACT_LINKS.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      aria-label={link.label}
                      title={`${link.label}: ${link.href.replace('mailto:', '')}`}
                      className="group/link flex items-center justify-center w-9 h-9 rounded-lg border border-neutral-200 dark:border-white/8 bg-neutral-50 text-neutral-500 dark:bg-white/[0.03] dark:text-neutral-400 transition-colors duration-200 hover:border-primary-500/40 hover:text-primary-500 dark:hover:border-primary-500/40 dark:hover:text-primary-400"
                    >
                      <link.icon size={15} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="group relative overflow-hidden border-t border-neutral-200 dark:border-white/8">
        <div className="flex w-max animate-marquee items-center py-3 group-hover:[animation-play-state:paused]">
          {[...MARQUEE_TECH, ...MARQUEE_TECH].map((tech, i) => (
            <span key={`${tech}-${i}`} className="flex items-center">
              <span className="px-6 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">
                {tech}
              </span>
              <span className="flex-shrink-0 h-1.5 w-1.5 rotate-45 bg-primary-500/40" />
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}

export default About